import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ShieldCheck } from "lucide-react";

export default function StatutorySetupTab() {
  const [tables, setTables] = useState([]);
  useEffect(() => { api.get("/hr/payroll/statutory-tables").then((r) => setTables(r.data)).catch(() => {}); }, []);

  const byType = (t) => tables.filter((x) => x.type === t);
  const Section = ({ title, type, children }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`statutory-${type}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {byType(type).map((t) => <Badge key={t.id} variant="outline" className="text-xs">{t.version} · from {t.effective_from}</Badge>)}
      </div>
      {children(byType(type)[0] || {})}
    </div>
  );

  return (
    <div data-testid="statutory-setup-tab" className="space-y-4">
      <div className="rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600 p-2.5 flex items-start gap-2">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Statutory rules are <b>versioned & effective-dated</b> — each payroll result preserves its table version, so future statutory changes never rewrite historical payroll.
      </div>

      <Section title="EPF (KWSP)" type="epf">
        {(t) => (
          <div className="text-sm space-y-1 text-slate-600">
            <p>Method: <b>KWSP Third Schedule</b> (banding + round-up).</p>
            <p>Employee rate: <b>{((t.ee_rate || 0) * 100).toFixed(0)}%</b> · Employer rate: <b>{((t.er_rate_low || 0) * 100).toFixed(0)}%</b> (≤ RM{t.er_threshold}) / <b>{((t.er_rate_high || 0) * 100).toFixed(0)}%</b> (&gt; RM{t.er_threshold}).</p>
            <p className="text-xs">Employee-specific EPF override supported (effective-dated & audited) via Payroll Profile.</p>
          </div>
        )}
      </Section>

      <Section title="SOCSO (PERKESO, Category 1)" type="socso">
        {(t) => (
          <div className="text-sm space-y-1 text-slate-600">
            <p>Employee: <b>{((t.ee_rate || 0) * 100).toFixed(1)}%</b> · Employer: <b>{((t.er_rate || 0) * 100).toFixed(2)}%</b> · Wage ceiling: <b>RM{t.ceiling}</b>.</p>
            <p className="text-xs">Uses the <b>official PERKESO Act 4 (Category 1) wage-band contribution schedule</b> (per RM100 band, rounded half-up to 5 sen) — reproduces the published table values.</p>
          </div>
        )}
      </Section>

      <Section title="EIS (SIP)" type="eis">
        {(t) => (
          <div className="text-sm space-y-1 text-slate-600">
            <p>Employee: <b>{((t.ee_rate || 0) * 100).toFixed(1)}%</b> · Employer: <b>{((t.er_rate || 0) * 100).toFixed(1)}%</b> · Wage ceiling: <b>RM{t.ceiling}</b>.</p>
            <p className="text-xs">Uses the <b>official EIS Act 800 wage-band contribution schedule</b> (per RM100 band, rounded half-up to 5 sen).</p>
          </div>
        )}
      </Section>

      <Section title="PCB / LHDN" type="pcb">
        {(t) => (
          <div className="text-sm space-y-1 text-slate-600">
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 flex items-start gap-1.5" data-testid="pcb-pending-banner">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span><b>PCB engine pending full LHDN rule implementation / validation.</b> The current figure is a provisional annualised estimate for shadow/comparison only — <b>NOT production</b>. ERP PCB production targets 2027; AutoCount remains the official PCB engine through Dec 2026.</span>
            </div>
            <p className="text-xs mt-1">Personal relief RM{t.personal_relief} · EPF relief cap RM{t.epf_relief_cap} · Child relief RM{t.child_relief} · Spouse relief RM{t.spouse_relief}.</p>
          </div>
        )}
      </Section>

      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="statutory-ul">
        <h3 className="text-sm font-semibold mb-1">Unpaid Leave (UL) Salary Deduction</h3>
        <p className="text-sm text-slate-600">Statutory default: <b>Employment Act 1955 Section 18A</b> — Monthly Wages ÷ Number of Days in the Wage Period × UL Days (uses the actual calendar days of the payroll month).</p>
        <p className="text-xs text-slate-500 mt-1">A fixed divisor (e.g. ÷26) is available only as a <b>NON-statutory configurable alternative</b> and is never the Malaysian default. UL salary deduction is shown transparently in Payroll Review (Monthly Wages, days in wage period, days eligible, UL days, rule, calculated amount) and is a <b>separate calculation</b> from the Attendance Allowance full forfeiture.</p>
      </div>
    </div>
  );
}
